import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { getBasedOnData, getSiblings, getStageForCompare } from "./utils/compareUtils";
import { getDevPrimaryImage, isCompleted, isStopped as isDevStopped } from "./utils/devCardUtils";
import { SmartImg } from "./utils/imageUtils";
import styles from "./DevCompareModal.module.css";

const PROGRESS_STAGES = ["Drawing", "Hand made", "Color sample", "Done"];
const STAGE_INDEX = { "Drawing stage": 0, "Hand made sample stage": 1, "Color sample stage": 2 };
const SOURCE_LABELS = {
  catalog: "Catalog item",
  fty: "Factory sample",
  brand: "Brand reference",
  cl: "Store model",
  other: "Other reference",
};

function normalizeDev(dev) {
  const data = dev?.data || {};
  return {
    ...data,
    ...dev,
    stage_status: dev?.stage_status || data.stage_status || "",
    full_status: dev?.full_status || data.full_status || "",
    stop_reason: dev?.stop_reason || data.stop_reason || "",
    completedAt: dev?.completedAt ?? data.completedAt,
  };
}

function stageTone(dev) {
  const item = normalizeDev(dev);
  if (isDevStopped(item)) return styles.toneStopped;
  if (isCompleted(item)) return styles.toneDone;
  if (item.stage_status === "Hand made sample stage") return styles.toneHand;
  if (item.stage_status === "Color sample stage") return styles.toneColor;
  return styles.toneDrawing;
}

function shortStatus(dev) {
  const item = normalizeDev(dev);
  if (isDevStopped(item)) return "Stopped";
  if (isCompleted(item)) return "Done";
  return (item.stage_status || "In progress").replace(" sample stage", "").replace(" stage", "");
}

function EyewearPlaceholder() {
  return (
    <svg className={styles.placeholderIcon} viewBox="0 0 160 64" aria-hidden="true">
      <path d="M12 28c7-9 48-10 55 0 3 5 0 20-6 24-8 5-28 4-35-1-6-5-10-18-14-23Zm136 0c-7-9-48-10-55 0-3 5 0 20 6 24 8 5 28 4 35-1 6-5 10-18 14-23Z" />
      <path d="M67 29c8-5 18-5 26 0M12 28 2 23m146 5 10-5" />
    </svg>
  );
}

function focusableElements(container) {
  if (!container) return [];
  return [...container.querySelectorAll('button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])')]
    .filter((element) => !element.hasAttribute("inert"));
}

export function DevCompareModal({ dev, catalogItems, allDevs, onClose }) {
  const [lightbox, setLightbox] = useState(null);
  const modalRef = useRef(null);
  const closeRef = useRef(null);
  const lightboxRef = useRef(null);
  const lightboxCloseRef = useRef(null);
  const returnFocusRef = useRef(null);
  const originFocusRef = useRef(null);

  const displayDev = normalizeDev(dev);
  const basedOn = getBasedOnData(displayDev, allDevs, catalogItems);
  const siblings = getSiblings(displayDev, allDevs);
  const stage = getStageForCompare(displayDev);
  const done = isCompleted(displayDev);
  const stopped = isDevStopped(displayDev);
  const stageStatus = displayDev.stage_status;
  const stageIndex = done ? 3 : (STAGE_INDEX[stageStatus] ?? -1);
  const devImage = getDevPrimaryImage(displayDev);
  const sourceImage = typeof basedOn?.imageUrl === "string" ? basedOn.imageUrl : basedOn?.imageUrl?.url;
  const sourceName = basedOn?.name?.trim().toLowerCase();
  const sourceMeta = [basedOn?.brand, basedOn?.ref]
    .filter((value) => value && value.trim().toLowerCase() !== sourceName)
    .join(" · ");
  const chips = [
    displayDev.material,
    displayDev.dev_type,
    displayDev.season && displayDev.year ? `${displayDev.season} ${displayDev.year}` : (displayDev.season || displayDev.year),
  ].filter(Boolean);
  const related = [displayDev, ...siblings].slice(0, 5);

  useEffect(() => {
    originFocusRef.current = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const frame = window.requestAnimationFrame(() => closeRef.current?.focus());
    return () => {
      window.cancelAnimationFrame(frame);
      document.body.style.overflow = previousOverflow;
      if (originFocusRef.current?.isConnected) originFocusRef.current.focus();
    };
  }, []);

  useEffect(() => {
    if (!lightbox) return undefined;
    const modalNode = modalRef.current;
    modalNode?.setAttribute("inert", "");
    modalNode?.setAttribute("aria-hidden", "true");
    const frame = window.requestAnimationFrame(() => lightboxCloseRef.current?.focus());
    return () => {
      window.cancelAnimationFrame(frame);
      modalNode?.removeAttribute("inert");
      modalNode?.removeAttribute("aria-hidden");
      if (returnFocusRef.current?.isConnected) returnFocusRef.current.focus();
    };
  }, [lightbox]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        if (lightbox) setLightbox(null);
        else onClose();
        return;
      }
      if (event.key !== "Tab") return;
      const container = lightbox ? lightboxRef.current : modalRef.current;
      const focusable = focusableElements(container);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [lightbox, onClose]);

  const openLightbox = (event, src, alt) => {
    if (!src) return;
    returnFocusRef.current = event.currentTarget;
    setLightbox({ src, alt });
  };

  const openOriginal = () => {
    onClose();
    window.dispatchEvent(new CustomEvent("open_catalog_item", { detail: { id: basedOn.item.id } }));
  };

  const modal = (
    <div className={`${styles.compareRoot} ${stageTone(displayDev)}`} onMouseDown={(event) => {
      if (event.target === event.currentTarget) onClose();
    }}>
      <section className={styles.modal} ref={modalRef} role="dialog" aria-modal="true" aria-labelledby="compare-title">
        <header className={styles.header}>
          <div>
            <h2 id="compare-title">Based on</h2>
            <p className={styles.subtitle}>Source reference compared with the current development</p>
          </div>
          <button ref={closeRef} className={styles.iconButton} type="button" onClick={onClose} aria-label="Close comparison">
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18" /></svg>
          </button>
        </header>

        <div className={styles.scrollArea}>
          <div className={styles.comparison}>
            <article className={styles.panel}>
              <div className={styles.panelHeading}>
                <span className={styles.panelKicker}>Source</span>
                <span className={styles.typeBadge}>{SOURCE_LABELS[basedOn?.type] || "Reference"}</span>
              </div>
              {sourceImage ? (
                <button className={styles.imageStage} type="button" onClick={(event) => openLightbox(event, sourceImage, basedOn?.name || "Source reference")} aria-label="Enlarge source image">
                  <SmartImg src={sourceImage} alt={basedOn?.name || "Source reference"} className={styles.photo} noTransform />
                  <span className={styles.zoomHint}><svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="6" /><path d="m16 16 4 4M11 8v6M8 11h6" /></svg>View larger</span>
                </button>
              ) : (
                <div className={`${styles.imageStage} ${styles.noImage}`}><EyewearPlaceholder /><span>Source image unavailable</span></div>
              )}
              <div className={styles.identity}>
                <h3>{basedOn?.name || "Source reference"}</h3>
                {sourceMeta && <p>{sourceMeta}</p>}
              </div>
              {basedOn?.item && <button className={styles.secondaryButton} type="button" onClick={openOriginal}>Open catalog item <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h14m-5-5 5 5-5 5" /></svg></button>}
            </article>

            <div className={styles.lineageAxis} aria-hidden="true">
              <span className={styles.axisLine} />
              <span className={styles.axisMark}>
                <svg viewBox="0 0 24 24"><path d="M5 12h14m-5-5 5 5-5 5" /></svg>
              </span>
              <span className={styles.axisLine} />
            </div>

            <article className={`${styles.panel} ${styles.panelCurrent}`}>
              <div className={styles.panelHeading}>
                <span className={styles.panelKicker}>Current development</span>
                <span className={styles.statusBadge}>{shortStatus(displayDev)}</span>
              </div>
              {devImage ? (
                <button className={styles.imageStage} type="button" onClick={(event) => openLightbox(event, devImage, displayDev.dev_number || "Current development")} aria-label="Enlarge development image">
                  <SmartImg src={devImage} alt={displayDev.dev_number || "Current development"} className={styles.photo} noTransform />
                  <span className={styles.zoomHint}><svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="6" /><path d="m16 16 4 4M11 8v6M8 11h6" /></svg>View larger</span>
                </button>
              ) : (
                <div className={`${styles.imageStage} ${styles.noImage}`}><EyewearPlaceholder /><span>Development image unavailable</span></div>
              )}
              <div className={styles.identity}>
                <div className={styles.devTitleRow}>
                  <h3>{displayDev.dev_number || "Untitled development"}</h3>
                  <span>{displayDev.fty || "Factory not set"}</span>
                </div>
                <p>{displayDev.designer || "Designer not set"}</p>
                {chips.length > 0 && <div className={styles.chips}>{chips.map((chip, index) => <span key={`${chip}-${index}`}>{chip}</span>)}</div>}
              </div>
            </article>
          </div>

          <section className={styles.progressSection} aria-label="Development progress">
            <div className={styles.sectionHeading}><span>Stage progress</span><strong>{stage.name}</strong></div>
            <div className={styles.progressTrack}>
              {PROGRESS_STAGES.map((label, index) => {
                const reached = index <= stageIndex;
                const current = !done && !stopped && index === stageIndex;
                return <div className={`${styles.progressStep} ${styles[`progressTone${index}`]} ${reached ? styles.reached : ""} ${current ? styles.current : ""}`} key={label} aria-current={current ? "step" : undefined}><i /><span>{label}</span></div>;
              })}
            </div>
            <div className={styles.statusMessage}><span className={styles.statusDot} />{displayDev.full_status || displayDev.stage_status || "Status not set"}</div>
          </section>

          {siblings.length > 0 && (
            <section className={styles.relatedSection} aria-labelledby="related-title">
              <div className={styles.relatedIntro}>
                <p className={styles.panelKicker}>Shared source</p>
                <h3 id="related-title">{siblings.length + 1} linked developments</h3>
                <p>The current model and up to four related developments from the same reference.</p>
              </div>
              <div className={styles.relatedList}>
                {related.map((item, index) => {
                  const image = getDevPrimaryImage(item);
                  return <div className={`${styles.relatedCard} ${stageTone(item)}`} key={item.id || index}>
                    <div className={styles.thumb}>{image ? <SmartImg src={image} alt="" className={styles.thumbImage} noTransform /> : <EyewearPlaceholder />}</div>
                    <div><strong>{item.dev_number || "Untitled"}</strong><span>{index === 0 ? "Current" : shortStatus(item)}</span></div>
                    <i aria-hidden="true" />
                  </div>;
                })}
              </div>
            </section>
          )}
        </div>

        <footer className={styles.footer}>
          <span><i /> Source linked to development {stage.number || ""}</span>
          <button className={styles.primaryButton} type="button" onClick={onClose}>Done comparing <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h14m-5-5 5 5-5 5" /></svg></button>
        </footer>
      </section>

      {lightbox && (
        <div className={styles.lightbox} ref={lightboxRef} role="dialog" aria-modal="true" aria-label={`${lightbox.alt} image preview`} onMouseDown={(event) => {
          if (event.target === event.currentTarget) setLightbox(null);
        }}>
          <button ref={lightboxCloseRef} className={`${styles.iconButton} ${styles.lightboxClose}`} type="button" onClick={() => setLightbox(null)} aria-label="Close image preview">
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18" /></svg>
          </button>
          <SmartImg src={lightbox.src} alt={lightbox.alt} className={styles.lightboxImage} noTransform priority />
          <p>{lightbox.alt}</p>
        </div>
      )}
    </div>
  );

  return createPortal(modal, document.body);
}
